<template>
  <div
    ref="root"
    class="rd rd--recipes rcp"
    :data-w="layout"
    :data-density="density"
    :data-bar="barSticky ? 'sticky' : 'flow'"
    :style="rootStyle"
  >
    <div class="rd-main">
      <report-nav :groups="ctl.menuGroups" :current="ctl.currentKey" :label="$t('recipes')" @select="openScreen">
        <template #tools>
          <button
            type="button"
            class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain"
            :title="`${$t('report_desktop.shortcuts')} (?)`"
            :aria-label="$t('report_desktop.shortcuts')"
            :aria-expanded="String(helpOpen)"
            @click="helpOpen = !helpOpen"
          >
            <v-icon size="18">mdi-keyboard-outline</v-icon>
          </button>
          <button type="button" class="ui-text-btn rd-nav__legacy" :title="$t('report_desktop.old_view')" @click="ctl.openLegacy">
            <v-icon size="16" color="#c2410c">mdi-history</v-icon>
            <span class="rd-nav__legacy-label">{{ $t("report_desktop.old_view") }}</span>
          </button>
        </template>
      </report-nav>

      <report-filter-bar
        ref="bar"
        :from="ctl.from"
        :to="ctl.to"
        :presets="ctl.presets"
        show-shifts
        :shifts="ctl.shifts"
        :search="ctl.search"
        :search-placeholder="$t('recipes_shell.search_placeholder')"
        :loading="ctl.busy"
        :export-disabled="!ctl.canExport"
        @period="ctl.setPeriod"
        @update:shifts="ctl.setShifts"
        @update:search="ctl.setSearch"
        @refresh="ctl.reload"
        @print="ctl.print"
        @excel="ctl.excel"
      >
        <template #filters>
          <recipe-picker></recipe-picker>
        </template>
        <template #actions>
          <button
            v-if="ctl.editTotal"
            type="button"
            class="rcp-edits"
            :aria-label="$t('recipes_shell.revert_all_n', { n: ctl.editTotal })"
            :title="$t('recipes_shell.revert_all_hint')"
            @click="ctl.revertAll"
          >
            <i class="rcp-dot"></i>
            <b>{{ ctl.pluralT("recipes_shell.edits_n", ctl.editTotal) }}</b>
            <v-icon size="15">mdi-undo-variant</v-icon>
          </button>
        </template>
      </report-filter-bar>

      <div class="rd-page">
        <div class="rd-head">
          <div class="rd-head__title">
            <h1>{{ $t("recipes") }}</h1>
            <p class="rd-head__sub">{{ ctl.headerSubtitle }}</p>
          </div>
          <div class="rd-head__right">
            <report-kpis :kpis="ctl.kpis"></report-kpis>
          </div>
        </div>

        <component :is="screen" :key="ctl.currentKey" @open-panel="openPanel"></component>
      </div>
    </div>

    <report-detail-panel :stack="panelStack" @close="closePanel" @back="panelBack" @open="openPanel"></report-detail-panel>
    <transition name="rd-fade">
      <div v-if="panelStack.length" class="rd-scrim" @click="closePanel"></div>
    </transition>

    <report-help-popover :open="helpOpen" :shortcuts="shortcuts" @close="helpOpen = false"></report-help-popover>
    <recipes-toast></recipes-toast>
    <recipes-dialog></recipes-dialog>
  </div>
</template>

<script>
import "@/components/report/desktop/reportDesktop.scss";
import "./recipesShell.scss";
import ReportNav from "@/components/report/shared/ReportNav.vue";
import ReportKpis from "@/components/report/shared/ReportKpis.vue";
import ReportHelpPopover from "@/components/report/shared/ReportHelpPopover.vue";
import ReportFilterBar from "@/components/report/desktop/ReportFilterBar.vue";
import ReportDetailPanel from "@/components/report/desktop/ReportDetailPanel.vue";
import hasReportShell from "@/components/report/shared/hasReportShell";
import RecipePicker from "./parts/RecipePicker.vue";
import RecipesDialog from "./parts/RecipesDialog.vue";
import RecipesToast from "./parts/RecipesToast.vue";
import RecipesByMeal from "./screens/RecipesByMeal.vue";
import RecipesTotal from "./screens/RecipesTotal.vue";

const baseKeydown = hasReportShell.methods.onKeydown;

/**
 * Recepti na računaru: gornja navigacija (Po jelima / Zbirno), ista traka filtera kao u izvještajima i magacinu
 * (period, smjene, jela, pretraga, radnje), zaglavlje sa brojkama, ekran i panel namirnice sa desne strane.
 * Stanje je u stranici (`recipesCtl`), ovdje je samo prikaz. Raspored po širini, panel, prečice i pomoć dolaze
 * iz hasReportShell.
 */
export default {
  name: "RecipesDesktop",
  mixins: [hasReportShell],
  components: {
    ReportNav,
    ReportKpis,
    ReportHelpPopover,
    ReportFilterBar,
    ReportDetailPanel,
    RecipePicker,
    RecipesDialog,
    RecipesToast,
    RecipesByMeal,
    RecipesTotal,
  },
  inject: { ctl: "recipesCtl" },
  computed: {
    currentKey() {
      return this.ctl.currentKey;
    },
    menuKeys() {
      return this.ctl.menuKeys;
    },
    screen() {
      return this.ctl.currentKey === "meals" ? "recipes-by-meal" : "recipes-total";
    },
    /** Prečice ove stranice (hasReportShell ima prečice izvještaja). */
    shortcuts() {
      return [
        { keys: "/", label: this.$t("recipes_shell.keys.search") },
        { keys: "r", label: this.$t("recipes_shell.keys.refresh") },
        { keys: "[  ]", label: this.$t("report_desktop.keys_day") },
        { keys: "1  2", label: this.$t("recipes_shell.keys.screens") },
        { keys: "↑  ↓", label: this.$t("recipes_shell.keys.portions") },
        { keys: "Esc", label: this.$t("report_desktop.keys_close") },
        { keys: "?", label: this.$t("report_desktop.shortcuts") },
      ];
    },
  },
  watch: {
    currentKey() {
      this.closePanel();
      window.scrollTo(0, 0);
    },
    "ctl.busy"(busy) {
      if (busy) {
        this.closePanel();
      }
    },
  },
  methods: {
    // hasReportShell poziva ove tri kad korisnik pritisne r, [ ili ] i broj
    reload() {
      this.ctl.reload();
    },
    shiftDay(step) {
      this.ctl.shiftDay(step);
    },
    openScreen(key) {
      this.ctl.openTab(key);
    },
    onKeydown(event) {
      // dijalog sam hvata Esc i Tab, a prečice stranice u njemu ne smiju raditi
      if (this.ctl.dialog) {
        return;
      }
      if (event.key === "Escape" && this.ctl.search && event.target && event.target.closest && event.target.closest(".rd-search")) {
        this.ctl.setSearch("");
        return;
      }
      baseKeydown.call(this, event);
    },
  },
};
</script>
